import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Form, Input, message, Typography } from 'antd';
import {
  LockOutlined,
  MailOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import userApi from '../../api/user';

const { Title, Text } = Typography;

function Login() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const rememberedEmail = useMemo(() => {
    try {
      return localStorage.getItem('tv_remember_email') || '';
    } catch {
      return '';
    }
  }, []);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      email: rememberedEmail,
      remember: Boolean(rememberedEmail),
    });
  }, [form, rememberedEmail]);

  const logoutOldSession = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('user');
  };

  const onFinish = async (values) => {
    const { email, password, remember } = values;

    try {
      setLoading(true);
      const response = await userApi.login({ email, password });

      if (response?.status === 200) {
        const { accessToken, refreshToken, user } = response.data.data;

        logoutOldSession();

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAdmin', user?.isAdmin || false);

        if (remember) localStorage.setItem('tv_remember_email', email);
        else localStorage.removeItem('tv_remember_email');

        message.success('Đăng nhập thành công');
        window.location.href = '/';
      } else {
        message.error('Sai tài khoản hoặc mật khẩu');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Đăng nhập thất bại. Vui lòng thử lại.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDownPassword = (e) => {
    // caps lock detection
    const isCaps = e.getModifierState && e.getModifierState('CapsLock');
    setCapsLock(Boolean(isCaps));
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        background:
          'radial-gradient(1200px circle at 10% 10%, rgba(22,119,255,0.25), transparent 40%), radial-gradient(1000px circle at 90% 20%, rgba(82,196,26,0.18), transparent 42%), radial-gradient(900px circle at 70% 90%, rgba(250,173,20,0.18), transparent 45%), linear-gradient(135deg, #001529 0%, #0b2a4a 60%, #001529 100%)',
      }}
    >
      {/* Card */}
      <div
        style={{
          width: '100%',
          maxWidth: 980,
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 30px 70px rgba(0,0,0,0.35)',
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {/* Left banner */}
        <div
          style={{
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background:
              'linear-gradient(135deg, rgba(22,119,255,0.18) 0%, rgba(255,255,255,0.06) 40%, rgba(82,196,26,0.10) 100%)',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: 'rgba(255,255,255,0.14)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  letterSpacing: 1,
                }}
              >
                TV
              </div>
              <div>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 800, fontSize: 16 }}>
                  Thiên Việt Admin
                </Text>
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                    Đăng nhập để quản trị hệ thống
                  </Text>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 28 }}>
              <Title level={2} style={{ color: '#fff', marginBottom: 6 }}>
                Welcome back 👋
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.75)' }}>
                Giao diện tối ưu, bảo mật và thân thiện.
              </Text>

              <div
                style={{
                  marginTop: 18,
                  padding: 14,
                  borderRadius: 16,
                  background: 'rgba(0,0,0,0.20)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <SafetyCertificateOutlined style={{ color: '#52c41a', marginTop: 2 }} />
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>
                    Tip bảo mật
                  </Text>
                  <div>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                      Không chia sẻ mật khẩu. Tick “Nhớ tài khoản” chỉ lưu email, không lưu password.
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Logo (optional) */}
          <div
            style={{
              height: 140,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.06)',
              border: '1px dashed rgba(255,255,255,0.18)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <div
              style={{
                width: '90%',
                height: '80%',
                backgroundImage:
                  'url(https://res.cloudinary.com/duxzkjxo5/image/upload/v1715507585/logo_kwouq4.png)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.25))',
              }}
            />
          </div>
        </div>

        {/* Right form */}
        <div style={{ padding: 28, background: 'rgba(255,255,255,0.88)' }}>
          <div style={{ marginBottom: 12 }}>
            <Title level={3} style={{ margin: 0 }}>
              Đăng nhập
            </Title>
            <Text type="secondary">Trang quản trị • nhập đúng tài khoản để tiếp tục</Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ email: rememberedEmail, remember: Boolean(rememberedEmail) }}
            requiredMark={false}
            style={{ marginTop: 18 }}
          >
            <Form.Item
              label="Email / Tài khoản"
              name="email"
              rules={[
                { required: true, message: 'Vui lòng nhập tài khoản' },
                { type: 'email', message: 'Email không hợp lệ' },
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="admin@gmail.com"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              label="Mật khẩu"
              name="password"
              rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="••••••••"
                autoComplete="current-password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                onKeyDown={onKeyDownPassword}
              />
            </Form.Item>

            {capsLock && (
              <div
                style={{
                  marginTop: -6,
                  marginBottom: 10,
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: 'rgba(250,173,20,0.16)',
                  border: '1px solid rgba(250,173,20,0.35)',
                  color: '#ad6800',
                  fontSize: 12,
                }}
              >
                Caps Lock đang bật — kiểm tra lại mật khẩu nhé.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Nhớ tài khoản</Checkbox>
              </Form.Item>

              <Button
                type="link"
                onClick={() => message.info('Liên hệ admin để cấp lại mật khẩu')}
                style={{ padding: 0 }}
              >
                Quên mật khẩu?
              </Button>
            </div>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={loading}
              disabled={loading}
              style={{
                width: '100%',
                marginTop: 16,
                height: 46,
                borderRadius: 14,
                boxShadow: '0 10px 22px rgba(22,119,255,0.25)',
              }}
            >
              {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </Button>

            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Nhấn <b>Enter</b> để đăng nhập nhanh
              </Text>
            </div>
          </Form>

          <div style={{ marginTop: 18, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              © {new Date().getFullYear()} Thiên Việt • Admin Panel
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
